import { useTranslation } from 'react-i18next';
import { TransactionsDto } from '@api/apiCompat';
import { MINUS_SIGN, PLUS_SIGN } from '@constants/common';
import DownloadInvoiceButton from '@pages/Settings/components/TransactionsTab/components/DownloadInvoiceButton';
import PurposeName from '@pages/Settings/components/TransactionsTab/components/PurposeName';
import { OperatorType } from '@pages/Settings/types';
import { format, parseISO } from 'date-fns';

import { SvgIconComponent } from '@mui/icons-material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import FolderSharedRoundedIcon from '@mui/icons-material/FolderSharedRounded';
import MonetizationOnRoundedIcon from '@mui/icons-material/MonetizationOnRounded';
import MoneyOffRoundedIcon from '@mui/icons-material/MoneyOffRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Typography } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';

type TransactionMapType = {
  [key in NonNullable<TransactionsDto['transaction_type']>]: {
    operator: OperatorType | string;
    typeIcon: SvgIconComponent;
    purposeIcon: SvgIconComponent;
    purposeLocale: string;
  };
};

const transactionMap: TransactionMapType = {
  campaign_start: {
    operator: MINUS_SIGN,
    typeIcon: PlayArrowRoundedIcon,
    purposeIcon: SearchIcon,
    purposeLocale: 'billing.transactions.campaignStartType',
  },
  campaign_refund: {
    operator: PLUS_SIGN,
    typeIcon: MoneyOffRoundedIcon,
    purposeIcon: SearchIcon,
    purposeLocale: 'billing.transactions.campaignRefundType',
  },
  subscription: {
    operator: PLUS_SIGN,
    typeIcon: AddRoundedIcon,
    purposeIcon: CheckCircleRoundedIcon,
    purposeLocale: 'billing.transactions.subscriptionType',
  },
  topup: {
    operator: PLUS_SIGN,
    typeIcon: AddRoundedIcon,
    purposeIcon: MonetizationOnRoundedIcon,
    purposeLocale: 'billing.transactions.topupType',
  },
  purchase_leads: {
    operator: '',
    typeIcon: MoneyOffRoundedIcon,
    purposeIcon: FolderSharedRoundedIcon,
    purposeLocale: 'billing.transactions.leadsDbType',
  },
  email_verification: {
    operator: '',
    typeIcon: MoneyOffRoundedIcon,
    purposeIcon: FolderSharedRoundedIcon,
    purposeLocale: 'billing.transactions.emailVerification',
  },
};

export const useTransactionsColumns = (): GridColDef[] => {
  const { t } = useTranslation();

  return [
    {
      field: 'purpose',
      headerName: 'Purpose',
      flex: 1,
      hideable: false,
      sortable: false,
      resizable: false,
      renderCell: ({ row }: GridCellParams<TransactionsDto>) => {
        const transactionType = row.transaction_type || 'topup';
        const PurposeIcon = transactionMap[transactionType]?.purposeIcon || SearchIcon;
        const purpose = transactionType;

        // Campaign-type transactions: show campaign name or fall back to description
        if (purpose?.includes('campaign')) {
          const label = row.campaign?.name || row.description || '';
          return (
            <Box pl={2} display="flex" gap={1} alignItems="center" color="grey">
              <PurposeIcon />
              <Typography variant="subtitle2" color="text.secondary" noWrap>
                {label}
              </Typography>
            </Box>
          );
        }

        // Billing transactions: subscription, topup, purchase_leads
        if (purpose === 'topup' || purpose === 'purchase_leads' || purpose === 'subscription') {
          return <PurposeName row={row} purpose={purpose} icon={<PurposeIcon />} />;
        }

        // Fallback for any other type: show description
        return (
          <Box pl={2} display="flex" gap={1} alignItems="center" color="grey">
            <PurposeIcon />
            <Typography variant="subtitle2" color="text.secondary" noWrap>
              {row.description || ''}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'type',
      headerName: 'Transaction type',
      flex: 1,
      hideable: false,
      sortable: false,
      resizable: false,
      minWidth: 140,
      renderCell: ({ row }: GridCellParams<TransactionsDto>) => {
        const transactionType = row.transaction_type ?? 'topup';
        const transaction = transactionMap[transactionType as keyof typeof transactionMap] || transactionMap.topup;
        const TransactionIcon = transaction.typeIcon;
        const { purposeLocale } = transaction;
        return (
          <Box pl={2} display="flex" gap={1} alignItems="center" color="grey">
            <TransactionIcon />
            <Typography variant="subtitle2" textAlign="center" color="text.secondary">
              {t(purposeLocale)}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'tokens',
      headerName: 'Tokens change',
      hideable: false,
      sortable: false,
      resizable: false,
      flex: 1,
      renderCell: ({ row }: GridCellParams<TransactionsDto>) => {
        const transactionType = row.transaction_type ?? 'topup';
        const { operator } = transactionMap[transactionType as keyof typeof transactionMap] || transactionMap.topup;
        return (
          <Box pl={2}>
            <Typography variant="body2" component="span" color={operator === PLUS_SIGN ? 'green' : 'red'}>
              {` ${operator}${row.paid_tokens}`}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      hideable: false,
      sortable: false,
      resizable: false,
      renderCell: ({ row }: GridCellParams<TransactionsDto>) => (
        <Box pl={2} color="grey">
          <Typography variant="subtitle2" textAlign="center">
            {row.created_at ? format(parseISO(row.created_at), 'dd MMMM yyyy HH:mm') : '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'invoice',
      headerName: 'Receipt',
      width: 80,
      hideable: false,
      sortable: false,
      resizable: false,
      renderCell: ({ row }: GridCellParams<TransactionsDto>) => (
        <Box pl={2} color="grey">
          {(row.receipt_url || row.invoice_id) && <DownloadInvoiceButton row={row} />}
        </Box>
      ),
    },
  ];
};
